import {Injectable} from "@angular/core";

export const SERVICE_CASE_MAIN_SOURCE = '/ui/service-case/main.mjs';

@Injectable({
  providedIn: 'root'
})
export class Util {
  private static readonly scriptLoadingPromises = new WeakMap<Document, Map<string, Promise<void>>>();

  /**
   * Check if a script element already exists in the DOM.
   * @param src The script element's source.
   * @param targetDocument The document where the script should be searched.
   */
  private static scriptElementAlreadyExists(src: string, targetDocument: Document): boolean {
    return targetDocument.querySelectorAll(`script[src="${src}"]`).length > 0;
  }

  /**
   * Attach a script element to the specified document. Will only be added once.
   * @param src The source URL of the script element to be added.
   * @param targetDocument The script element will be attached to the specified document's head.
   * @param asModule Force loading the script as an ES module (`type="module"`).
   *   By default this is inferred from a `.mjs` file extension, but some CDN loaders
   *   are served as `.js` while still relying on module features such as `import.meta`.
   */
  static attachScript(src: string, targetDocument?: Document, asModule?: boolean): Promise<void> {
    targetDocument = targetDocument || document;
    const scriptLoadingPromises = Util.getScriptLoadingPromises(targetDocument);
    const existingScriptLoadingPromise = scriptLoadingPromises.get(src);

    if (existingScriptLoadingPromise !== undefined && Util.scriptElementAlreadyExists(src, targetDocument)) {
      Util.logSharedServiceCaseModuleLoad(src);
      return existingScriptLoadingPromise;
    }
    scriptLoadingPromises.delete(src);

    if (Util.scriptElementAlreadyExists(src, targetDocument)) {
      return Promise.resolve();
    }
    if (!targetDocument.head) {
      return Promise.reject(new Error('Util: Failed to attach script ' + src + ' as target document has no head.'));
    }

    const script = targetDocument.createElement('script');
    script.type = asModule || src.endsWith('.mjs') ? 'module' : 'text/javascript';
    script.src = src;
    Util.logServiceCaseModuleLoading(src);

    const scriptLoadingPromise = new Promise<void>((resolve, reject) => {
      script.onload = (): void => {
        Util.logServiceCaseModuleLoaded(src);
        resolve();
      };
      script.onerror = (): void => {
        script.parentElement?.removeChild(script);
        scriptLoadingPromises.delete(src);
        Util.logServiceCaseModuleLoadingFailed(src);
        reject(new Error('Util: Failed to load script ' + script.src));
      };
    });
    scriptLoadingPromises.set(src, scriptLoadingPromise);
    targetDocument.head.appendChild(script);

    return scriptLoadingPromise;
  }

  private static getScriptLoadingPromises(targetDocument: Document): Map<string, Promise<void>> {
    let scriptLoadingPromises = Util.scriptLoadingPromises.get(targetDocument);
    if (!scriptLoadingPromises) {
      scriptLoadingPromises = new Map<string, Promise<void>>();
      Util.scriptLoadingPromises.set(targetDocument, scriptLoadingPromises);
    }
    return scriptLoadingPromises;
  }

  private static logServiceCaseModuleLoading(src: string): void {
    if (src === SERVICE_CASE_MAIN_SOURCE) {
      console.info(Util.logPrefix('Webapp', 'Service case module') + 'Starting module load.');
    }
  }

  private static logSharedServiceCaseModuleLoad(src: string): void {
    if (src === SERVICE_CASE_MAIN_SOURCE) {
      console.info(Util.logPrefix('Webapp', 'Service case module') + 'Waiting for the existing module load.');
    }
  }

  private static logServiceCaseModuleLoaded(src: string): void {
    if (src === SERVICE_CASE_MAIN_SOURCE) {
      const resourceTiming = performance.getEntriesByName(new URL(src, window.location.origin).href)
        .at(-1) as PerformanceResourceTiming | undefined;
      console.info(Util.logPrefix('Webapp', 'Service case module')
        + `Module loaded. Registered elements: details=${!!customElements.get('ueg-awp-service-case-details')}, `
        + `overview=${!!customElements.get('ueg-awp-service-case-overview')}, `
        + `shortlist=${!!customElements.get('ueg-awp-service-case-shortlist')}. `
        + `Resource timing: duration=${Math.round(resourceTiming?.duration || 0)}ms, `
        + `transferSize=${resourceTiming?.transferSize ?? 'unavailable'}, `
        + `encodedBodySize=${resourceTiming?.encodedBodySize ?? 'unavailable'}.`);
    }
  }

  private static logServiceCaseModuleLoadingFailed(src: string): void {
    if (src === SERVICE_CASE_MAIN_SOURCE) {
      console.error(Util.logPrefix('Webapp', 'Service case module') + 'Module loading failed.');
    }
  }

  /**
   * Creates a new UUID using a randomly generated string.
   */
  static createRandomUuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Util.getCryptoRandom() * 16) | 0; // eslint-disable-line no-bitwise
      const v = c === 'x' ? r : (r & 0x3) | 0x8; // eslint-disable-line no-bitwise
      return v.toString(16);
    });
  }

  static getCryptoRandom(): number {
    return window.crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32;
  }

  /**
   * Returns a log line prefix in the form of "YYYY-MM-DDT00:00:00.000Z [Component] [Subcomponent] ".
   * @param component Short, friendly name of the component that logs the line.
   * @param subcomponent Short, friendly name of the subcomponent that logs the line.
   */
  static logPrefix(component?: string, subcomponent?: string): string {
    const componentString = component ? `[${component}] ` : '';
    const subcomponentString = subcomponent ? `[${subcomponent}] ` : '';
    const datetimeString = (new Date()).toISOString() + ' ';
    return datetimeString + componentString + subcomponentString;
  }
}
